import { LocationVarInheritance } from "@swivl-mono/types";

import { CortexInheritancePill } from "../CortexInheritancePill";

export type ScheduleScopeKind = "org" | "group" | "location";

interface InheritancePillProps {
  /** Scope the winning row actually lives at, or undefined when nothing is set. */
  source: ScheduleScopeKind | undefined;
  /** Scope the page is editing — decides whether `source` counts as inherited. */
  currentScope: ScheduleScopeKind;
  /**
   * True for values that fall back to a built-in default when nothing is set at
   * any scope (timezone does, via VarDef). Schedules have no such tier: nothing
   * set means the bot reports the facility closed, which is a gap rather than a
   * default, so those render no tag at all and let the body text say so.
   */
  fallsBackToDefault?: boolean;
}

/**
 * Provenance tag for a schedule / holiday row, using the same tag as the Cortex
 * pages so Details v3 speaks one visual language.
 *
 * The wording differs in one place. Cortex vars sit above a `VarDef` default, so
 * a var set at org is a customization of that default. Schedules have nothing
 * beneath org, so an org row isn't customizing anything — it's the baseline every
 * group and location inherits, and it reads "Org Default". Group and location
 * rows do have a parent to differ from, so they keep "Customized".
 */
export function InheritancePill({ source, currentScope, fallsBackToDefault }: InheritancePillProps): React.ReactElement | null {
  if (source === undefined) {
    return fallsBackToDefault ? <CortexInheritancePill inheritance={LocationVarInheritance.DEFAULT} /> : null;
  }

  if (source === currentScope) {
    // An org row is a customization when a built-in default sits beneath it (schedule
    // groups since ENG-3216 decision 49, the timezone always); "Org Default" only when
    // nothing does.
    return currentScope === "org" && !fallsBackToDefault ? (
      <CortexInheritancePill inheritance={LocationVarInheritance.DEFAULT} label="Org Default" />
    ) : (
      <CortexInheritancePill inheritance={LocationVarInheritance.NONE} />
    );
  }

  return <CortexInheritancePill inheritance={source === "group" ? LocationVarInheritance.GROUP : LocationVarInheritance.ORG} />;
}
